/**
 * Thin API client.
 *
 * Every money movement and order carries an idempotency key, error shapes are
 * unwrapped consistently, and the token is attached from local session state
 * (build guide / 11).
 */

const RAW_API_URL = import.meta.env.VITE_API_URL || "";
const API_HOST = RAW_API_URL.replace(/\/+$/, "");
const BASE = API_HOST ? `${API_HOST}/api/v1` : "/api/v1";

export function resolveFileUrl(url: string | null | undefined): string {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return API_HOST ? `${API_HOST}${url.startsWith("/") ? "" : "/"}${url}` : url;
}

export class ApiClientError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly fields?: Record<string, string>,
    public readonly requestId?: string,
  ) {
    super(message);
  }

  get isPermissionDenied() {
    return this.status === 403;
  }
  get isConflict() {
    return this.status === 409;
  }
}

const TOKEN_KEY = "autoshop.token";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(token: string | null) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

type RequestOptions = {
  method?: string;
  body?: unknown;
  /** Omit auth for the public customer approval pages. */
  anonymous?: boolean;
  signal?: AbortSignal;
};

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };

  if (!options.anonymous) {
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${BASE}${path}`, {
    method: options.method ?? "GET",
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    signal: options.signal,
  });

  if (response.status === 204) return undefined as T;

  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    const error = payload?.error ?? {};
    // A revoked or expired session sends the user back to sign-in rather than
    // leaving the app in a half-authorised state.
    if (response.status === 401) {
      setToken(null);
      if (!window.location.pathname.startsWith("/approve")) {
        window.location.href = "/login";
      }
    }
    throw new ApiClientError(
      response.status,
      error.code ?? "unknown",
      error.message ?? "Something went wrong.",
      error.fields,
      payload?.requestId,
    );
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string, opts?: RequestOptions) => request<T>(path, opts),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: "POST", body }),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: "PATCH", body }),
  put: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: "PUT", body }),
  delete: <T>(path: string, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: "DELETE" }),
};

/**
 * Upload an image as a raw body.
 *
 * Deliberately not JSON: base64 inflates a phone photo by a third, and the
 * server sniffs the real format from the bytes anyway.
 */
export async function uploadImage(
  path: string,
  file: File,
): Promise<{ key: string; url: string; contentType: string; size: number }> {
  const token = getToken();
  const response = await fetch(`${BASE}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": file.type || "image/jpeg",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: file,
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = payload?.error ?? {};
    throw new ApiClientError(
      response.status,
      error.code ?? "upload_failed",
      error.message ?? "That photo could not be uploaded.",
      error.fields,
      payload?.requestId,
    );
  }
  return payload;
}

/**
 * A stable key for one intended money movement.
 *
 * Generated once when the operator opens the payment form, not per click, so a
 * double submit or a retry resolves to the same attempt (build guide / 09).
 */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}
